//
import type { AxiosCollection } from '@/types/Components/Response'
import type {
  Assignment,
  AssignmentResponse,
  HistoryItem,
  IntegrationLogItem,
  StatusType,
  ValidationErrorSuggestion,
} from '@/types/Models/Assignment'
import type { AssignmentSummaryStoreState } from '@/types/Store'
import { notify } from '@kyvg/vue3-notification'
import { defineStore } from 'pinia'
import { CANCELLED, COMPLETE, EDITABLE_STATUSES, PENDING } from '@/const/AssignmentStatuses'
import { LABELS_CREATED_SUCCESS, LABELS_CREATED_WITH_ERRORS } from '@/const/LabelsCreatedStatuses'
import { LoadingStatus } from '@/const/LoadingStatus'
import { catchErrors } from '@/services/Helpers'
import AssignmentProxy from '@/proxies/AssignmentProxy'
import AssignmentStatusProxy from '@/proxies/AssignmentStatusProxy'
import AssignmentTransformer from '@/transformers/AssignmentTransformer'

type HandleAssignmentActionOptions = {
  action: (assignmentId?: string) => Promise<any>
  isInitialLoad: boolean
  failOnRejection: boolean
}

const createDefaultState = (): AssignmentSummaryStoreState => ({
  loadingStatus: LoadingStatus.PENDING,
  assignment: null,
  labelsStatuses: null,
  validationErrors: [],
  history: [],
  historyLoading: false,
  historyCount: 0,
  integrationLogs: [],
  integrationLogsLoading: false,
  integrationLogsCount: 0,
})

export const useAssignmentSummaryStore = defineStore('assignmentSummary', {
  state: (): AssignmentSummaryStoreState => createDefaultState(),
  getters: {
    id: (state: AssignmentSummaryStoreState) => state.assignment?.id,
    reference: (state: AssignmentSummaryStoreState) => state.assignment?.reference,
    assignmentStatus: (state: AssignmentSummaryStoreState) => state.assignment?.status || '',
    getValidationErrors: (state: AssignmentSummaryStoreState): ValidationErrorSuggestion[] => {
      const out: ValidationErrorSuggestion[] = []
      let currentId: string | null = null

      for (const row of state.validationErrors ?? []) {
        if (row.itemId !== currentId) {
          out.push({
            itemId: row.itemId,
            itemTitle: row.itemTitle,
            itemReference: row.itemReference,
            flagId: '',
            field: '',
            level: '',
            value: '',
            reason: '',
            newValue: '',
            newReason: '',
          } as ValidationErrorSuggestion)
          currentId = row.itemId
        }
        out.push(row)
      }

      return out
    },
    getLabelsSuccessCreatedCount: ({ assignment }: AssignmentSummaryStoreState) =>
      !assignment ? 0 : assignment.itemsCount - assignment.labelsErrorsCount,
    getLabelsNotCreatedCount: ({ assignment }: AssignmentSummaryStoreState) => assignment?.labelsErrorsCount || 0,
    labelsCreatedStatus: ({ assignment }: AssignmentSummaryStoreState) =>
      assignment && assignment.labelsErrorsCount > 0 ? LABELS_CREATED_WITH_ERRORS : LABELS_CREATED_SUCCESS,
    senderName: ({ assignment }: AssignmentSummaryStoreState) =>
      !assignment ? '' : assignment.senderAddress?.companyName || assignment.company?.name || '',
    senderShortName: (state: AssignmentSummaryStoreState) => state.assignment?.company?.shortName || '',
    isItemsEditable: (state: AssignmentSummaryStoreState) => {
      const status = state.assignment?.archived ? CANCELLED : state.assignment?.status
      if (!status) {
        return false
      }

      return EDITABLE_STATUSES.includes(status)
    },
    documentsUrl: (state: AssignmentSummaryStoreState) => state.assignment?.documentsUrl ?? null,
    domesticErrorsCount: (state: AssignmentSummaryStoreState) => state.assignment?.domesticErrorsCount ?? 0,
    internationalErrorsCount: (state: AssignmentSummaryStoreState) => state.assignment?.internationalErrorsCount ?? 0,
    isFinishedStatus: (state: AssignmentSummaryStoreState) =>
      [COMPLETE, CANCELLED].includes(state.assignment?.status || PENDING),
  },
  actions: {
    setAssignment(data: AssignmentResponse) {
      this.assignment = AssignmentTransformer.fetch(data.data)
      this.loadingStatus = LoadingStatus.SUCCESS
      return this.assignment
    },
    partialUpdate(payload: Partial<Assignment>) {
      if (this.assignment) {
        Object.assign(this.assignment, payload)
      }
    },
    resetState() {
      Object.assign(this, createDefaultState())
    },
    resetErrors() {
      this.validationErrors = []
    },
    async handleAssignmentAction(options: HandleAssignmentActionOptions) {
      if (options.isInitialLoad) {
        this.loadingStatus = LoadingStatus.LOADING
      } else if (!this.assignment) {
        throw new Error('Cannot perform action without assignment')
      }

      try {
        const response = await options.action(this.assignment?.id)
        if (!response) {
          return undefined
        }

        const data = AssignmentTransformer.fetch(response.data)
        this.assignment = data
        this.loadingStatus = LoadingStatus.SUCCESS
        return data
      } catch (error) {
        if (options.failOnRejection) {
          this.loadingStatus = LoadingStatus.FAILURE
        }
        throw error
      }
    },
    fetch(id: string) {
      return this.handleAssignmentAction({
        action: () => new AssignmentProxy().getAssignmentDetails(id),
        isInitialLoad: true,
        failOnRejection: true,
      }).catch(catchErrors)
    },
    refresh() {
      return this.handleAssignmentAction({
        action: async (assignmentId) =>
          assignmentId && (await new AssignmentProxy().getAssignmentDetails(assignmentId)),
        isInitialLoad: false,
        failOnRejection: false,
      }).catch(catchErrors)
    },
    updateStatus({ newStatus, assignmentId }: { newStatus: string; assignmentId: string }) {
      return new AssignmentStatusProxy(assignmentId)
        .resetTo(newStatus)
        .then(() => {
          if (this.assignment) {
            this.assignment.status = newStatus as StatusType
          }
        })
        .catch(catchErrors)
    },
    fetchHistory({ id, filter }: { id: string; filter: object }) {
      const assignmentId = id || this.assignment?.id
      if (!assignmentId) {
        return undefined
      }

      this.historyLoading = true

      return new AssignmentProxy(filter)
        .getHistory(assignmentId)
        .then((response: AxiosCollection<HistoryItem>) => {
          this.history = response.data.length ? response.data : []
          this.historyCount = response.count
          return response
        })
        .catch(catchErrors)
        .finally(() => {
          this.historyLoading = false
        })
    },
    fetchIntegrationLogs({ id, filter }: { id: string; filter: object }) {
      const assignmentId = id || this.assignment?.id
      if (!assignmentId) {
        return undefined
      }

      this.integrationLogsLoading = true

      return new AssignmentProxy(filter)
        .getIntegrationLogs(assignmentId)
        .then((response: AxiosCollection<IntegrationLogItem>) => {
          this.integrationLogs = response.data.length ? response.data : []
          this.integrationLogsCount = response.count
          return response
        })
        .catch(catchErrors)
        .finally(() => {
          this.integrationLogsLoading = false
        })
    },
    fetchStatus(payload?: any) {
      const id = payload || this.assignment?.id
      if (!id) {
        return undefined
      }

      return new AssignmentProxy()
        .getStatus(id)
        .then((response) => {
          if (response.data.status) {
            if (this.assignment) {
              this.assignment.status = response.data.status
            }
          }
          return response
        })
        .catch(catchErrors)
    },
    fetchLabelsStatuses(payload?: any) {
      const assignmentId = payload || this.assignment?.id
      if (!assignmentId) {
        return Promise.resolve()
      }

      return new AssignmentProxy().getLabelsStatuses(assignmentId).then((response) => {
        if (response.data) {
          this.labelsStatuses = response.data
        }
      })
    },
    reset() {
      this.resetState()
    },
    fetchValidationErrors(level: string) {
      this.resetErrors()

      if (!this.assignment?.id) {
        return
      }

      return new AssignmentProxy()
        .getErrorSuggestions(this.assignment.id, level)
        .then((response: AxiosCollection<ValidationErrorSuggestion>) => {
          this.validationErrors = response.data
          return response
        })
        .catch(catchErrors)
    },
    clearValidationErrors() {
      this.resetErrors()
    },
    applyValidationErrors(payload: any) {
      if (!this.assignment?.id) {
        return undefined
      }

      return new AssignmentProxy()
        .applyErrorSuggestions(this.assignment.id, payload)
        .then((response) => {
          notify({ text: 'Validation errors applied successfully. Validation started.', group: 'success' })
          return response
        })
        .catch(catchErrors)
    },
    submitProcess() {
      return this.handleAssignmentAction({
        action: async (assignmentId) => assignmentId && new AssignmentStatusProxy(assignmentId).submitProcess(),
        isInitialLoad: false,
        failOnRejection: false,
      }).catch(catchErrors)
    },
    submitOptimize() {
      return this.handleAssignmentAction({
        action: async (assignmentId) => assignmentId && new AssignmentProxy().submitOptimize(assignmentId),
        isInitialLoad: false,
        failOnRejection: false,
      }).catch(catchErrors)
    },
    submitComplete() {
      return this.handleAssignmentAction({
        action: async (assignmentId) => assignmentId && new AssignmentStatusProxy(assignmentId).submitComplete(),
        isInitialLoad: false,
        failOnRejection: false,
      }).catch(catchErrors)
    },
    submitReportAccept() {
      return this.handleAssignmentAction({
        action: async (assignmentId) => assignmentId && new AssignmentStatusProxy(assignmentId).submitReportAccept(),
        isInitialLoad: false,
        failOnRejection: false,
      }).catch(catchErrors)
    },
    submitReportDeny() {
      return this.handleAssignmentAction({
        action: async (assignmentId) => assignmentId && new AssignmentStatusProxy(assignmentId).submitReportDeny(),
        isInitialLoad: false,
        failOnRejection: false,
      }).catch(catchErrors)
    },
    createLabels() {
      return this.handleAssignmentAction({
        action: async (assignmentId) => assignmentId && new AssignmentProxy().createLabels(assignmentId),
        isInitialLoad: false,
        failOnRejection: false,
      }).catch(catchErrors)
    },
  },
})

export type AssignmentSummaryStore = ReturnType<typeof useAssignmentSummaryStore>
